<template>
  <div>
    <base-header class="pb-9" type="danger"> </base-header>
    <div v-if="!isLivestreamAdmin" class="container-fluid mt--8">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-body text-muted">
              You need livestream admin access to view stream reporting.
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="container-fluid mt--8">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header">
              <div class="h3 mb-0">Stream Reporting</div>
            </div>
            <div class="card-body mb--4">
              <div v-if="sessionsLoadError" class="alert alert-danger small py-2 mb-3">
                {{ sessionsLoadError }}
              </div>
              <div class="row align-items-center">
                <div class="col-4">
                  <base-input class="select-danger">
                    <el-select
                      v-model="filters"
                      filterable
                      multiple
                      placeholder="Add filters"
                    >
                      <el-option
                        v-for="option in filterOptionArray"
                        :key="option.field"
                        class="select-danger"
                        :label="option.label"
                        :value="option.field"
                      />
                    </el-select>
                  </base-input>
                </div>
              </div>
              <div class="row align-items-center">
                <div class="col-4" v-for="filter in activeFilters" :key="filter">
                  <filter-selector
                    :selection="filterOptions[filter]"
                    @valueChange="filterValues[filter] = $event"
                  />
                </div>
                <div class="col-auto" v-if="activeFilters.length > 0">
                  <base-input>
                    <base-button @click="runQuery">Fetch</base-button>
                  </base-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
            <el-table
              class="matchesTable"
              :data="paginatedMatches"
              row-key="id"
              header-row-class-name="thead-light"
              :default-sort="{ prop: 'startDate', order: 'descending' }"
            >
              <el-table-column type="expand" width="40">
                <template v-slot="{ row }">
                  <div class="px-3 py-2">
                    <div v-if="sessionsLoading && !sessionsForMatch(row).length" class="small text-muted">
                      Loading stream sessions…
                    </div>
                    <div v-else-if="!sessionsForMatch(row).length" class="small text-muted">
                      No stream sessions recorded for this match.
                    </div>
                    <el-table
                      v-else
                      class="session-subtable"
                      :data="sessionsForMatch(row)"
                      row-key="id"
                      size="mini"
                      header-row-class-name="thead-light"
                    >
                      <el-table-column label="Status" width="90">
                        <template v-slot="{ row: session }">
                          <span :class="sessionStatusClass(session)" class="badge">
                            {{ sessionStatusLabel(session) }}
                          </span>
                        </template>
                      </el-table-column>
                      <el-table-column label="Connected" min-width="150">
                        <template v-slot="{ row: session }">
                          {{ sessionTimestamp(session.flow_started_at || session.session_started_at || session.activated_at) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Ended" min-width="150">
                        <template v-slot="{ row: session }">
                          {{ sessionTimestamp(session.flow_ended_at || session.session_ended_at) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Duration" width="100">
                        <template v-slot="{ row: session }">
                          {{ formatStreamDuration(session.total_stream_duration_ms || session.session_active_duration_ms) || '—' }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Input" min-width="130">
                        <template v-slot="{ row: session }">
                          {{ sessionInputLabel(session) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Takers" min-width="160">
                        <template v-slot="{ row: session }">
                          {{ sessionTakersLabel(session) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="BBC" width="70" align="center">
                        <template v-slot="{ row: session }">
                          {{ session.bbc_comms_declared == null ? '—' : session.bbc_comms_declared ? 'Yes' : 'No' }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Commercial" width="95" align="center">
                        <template v-slot="{ row: session }">
                          {{ session.commercialised_declared == null ? '—' : session.commercialised_declared ? 'Yes' : 'No' }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Shutdown" min-width="150">
                        <template v-slot="{ row: session }">
                          {{ shutdownReasonLabel(session.session_shutdown_reason) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="Activated by" min-width="140">
                        <template v-slot="{ row: session }">
                          {{ session.activated_by_display_name || session.activated_by_email || '—' }}
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Home" prop="team1Name" min-width="120" />
              <el-table-column label="Away" prop="team2Name" min-width="120" />
              <el-table-column label="Date" prop="startDate" width="120" sortable>
                <template v-slot="{ row }">
                  {{ row.startDate | date }}
                </template>
              </el-table-column>
              <el-table-column label="Venue" prop="ground" min-width="130">
                <template v-slot="{ row }">
                  <venue-look-up :venueID="row.ground"></venue-look-up>
                </template>
              </el-table-column>
              <el-table-column label="Comp Name" prop="competitionName" min-width="140" sortable>
                <template v-slot="{ row }">
                  {{ row.competitionName || row.competition || "" }}
                </template>
              </el-table-column>
              <el-table-column label="Sessions" width="90" align="center">
                <template v-slot="{ row }">
                  <span v-if="sessionsLoading && sessionsForMatch(row).length === 0" class="text-muted">…</span>
                  <span v-else>{{ sessionsForMatch(row).length }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Input" min-width="170">
                <template v-slot="{ row }">
                  <span class="small">{{ inputNameForMatch(row) || "None" }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Takers" min-width="200">
                <template v-slot="{ row }">
                  <span v-if="!matchTakerRows(row).length" class="text-muted">None</span>
                  <span v-else class="small">
                    {{ matchTakerRows(row).map((r) => takerLabel(r.takerId)).join(', ') }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="" width="110" align="right">
                <template v-slot="{ row }">
                  <router-link
                    class="btn btn-sm btn-outline-primary mb-0"
                    :to="{ name: 'Match Assets', params: { id: matchDocId(row) }, query: { tab: 'live' } }"
                  >
                    Open Live
                  </router-link>
                </template>
              </el-table-column>
            </el-table>

            <div
              slot="footer"
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <div>
                <p class="card-category">
                  Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
                </p>
              </div>
              <base-pagination
                class="pagination-no-border"
                v-model="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="total"
              />
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FilterSelector from "./Components/FilterSelector";
import VenueLookUp from "./Components/VenueLookUp";
import { Firebase } from "@/firebase.js";

export default {
  name: "StreamReporting",
  components: {
    "venue-look-up": VenueLookUp,
    "filter-selector": FilterSelector,
  },
  data() {
    const year = new Date().getFullYear();
    return {
      pagination: {
        perPage: 25,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0,
      },
      requestedVenueIds: {},
      sessionsByMatchId: {},
      sessionsLoading: false,
      sessionsLoadError: "",
      sessionsLoadTimer: null,
      filters: ["year"],
      filterValues: { year },
      filterOptions: {
        year: { field: "year", label: "Year", type: "number" },
        cricketFormat: {
          field: "cricketFormat",
          label: "Format",
          collection: "matchFormats",
          collectionLabel: "name",
          collectionGetter: "formats",
          type: "single-select",
        },
        cricketType: {
          field: "cricketType",
          label: "Type",
          collection: "matchFormats",
          collectionLabel: "name",
          collectionGetter: "types",
          type: "single-select",
        },
        int: { field: "int", label: "International", type: "boolean" },
        teamsMap: {
          field: "teamsMap",
          collection: "teams",
          collectionLabel: "teamName",
          label: "Teams",
          type: "multi-select",
        },
        team1Name: {
          field: "team1Name",
          label: "Home Team",
          collection: "teams",
          collectionLabel: "teamName",
          collectionValue: "teamName",
          type: "single-select",
        },
        team2Name: {
          field: "team2Name",
          label: "Away Team",
          collection: "teams",
          collectionLabel: "teamName",
          collectionValue: "teamName",
          type: "single-select",
        },
        ground: {
          field: "ground",
          collection: "venues",
          collectionLabel: "name",
          label: "Venue",
          type: "single-select",
        },
      },
    };
  },
  computed: {
    user() {
      return this.$store.getters["user/user"];
    },
    isLivestreamAdmin() {
      return Boolean(this.user.roles && this.user.roles.livestream_admin);
    },
    matches() {
      return this.$store.getters["matches/array"];
    },
    filterOptionArray() {
      return Object.values(this.filterOptions);
    },
    activeFilters() {
      return (this.filters || []).filter((field) => !!this.filterOptions[field]);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) highBound = this.total;
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.matches.length;
    },
    sortedMatches() {
      return [...this.matches].sort((a, b) => this.matchStartMs(b) - this.matchStartMs(a));
    },
    paginatedMatches() {
      return this.sortedMatches.slice(this.from, this.from + this.pagination.perPage);
    },
    paginatedMatchIdsKey() {
      return this.paginatedMatches
        .map((row) => this.matchDocId(row))
        .filter(Boolean)
        .join(",");
    },
  },
  watch: {
    paginatedMatches: {
      immediate: true,
      handler(rows) {
        this.prefetchMissingVenues(rows || []);
      },
    },
    paginatedMatchIdsKey: {
      immediate: true,
      handler(key) {
        const ids = key ? key.split(",").filter(Boolean) : [];
        this.scheduleSessionLoad(ids);
      },
    },
    total(total) {
      const maxPage = Math.max(1, Math.ceil(total / this.pagination.perPage) || 1);
      if (this.pagination.currentPage > maxPage) {
        this.pagination.currentPage = maxPage;
      }
    },
    "user.roles": {
      deep: true,
      immediate: true,
      handler(roles) {
        if (roles && roles.livestream_admin) {
          this.$store.dispatch("takers/fetchAndAdd");
          this.$store.dispatch("inputs/fetchAndAdd");
        }
      },
    },
  },
  beforeMount() {
    if (!this.isLivestreamAdmin) return;
    this.$store.dispatch("teams/fetchAndAdd", { limit: 0 });
    this.$store.dispatch("venues/fetchAndAdd");
    this.fetchPastMatches();
  },
  beforeDestroy() {
    if (this.sessionsLoadTimer) {
      clearTimeout(this.sessionsLoadTimer);
      this.sessionsLoadTimer = null;
    }
  },
  methods: {
    matchStartMs(row) {
      if (!row || !row.startDate) return 0;
      const d = row.startDate.toDate ? row.startDate.toDate() : new Date(row.startDate);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    },
    matchDocId(row) {
      if (!row) return null;
      if (row.id) return String(row.id);
      const data = this.$store.state.matches?.data || {};
      for (const [id, doc] of Object.entries(data)) {
        if (doc === row) return id;
      }
      return null;
    },
    scheduleSessionLoad(matchIds) {
      if (this.sessionsLoadTimer) clearTimeout(this.sessionsLoadTimer);
      this.sessionsLoadTimer = setTimeout(() => {
        this.sessionsLoadTimer = null;
        this.loadSessionsForMatchIds(matchIds);
      }, 300);
    },
    startOfToday() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    },
    fetchPastMatches() {
      this.sessionsByMatchId = {};
      this.$store.commit("matches/RESET_VUEX_EASY_FIRESTORE_STATE");
      this.$store.dispatch("matches/fetchAndAdd", {
        clauses: { where: this.buildPastWhere(), limit: 500 },
      });
    },
    buildPastWhere() {
      const where = [["startDate", "<", this.startOfToday()]];
      this.activeFilters.forEach((field) => {
        const value = this.filterValues[field];
        const option = this.filterOptions[field];
        if (value === undefined || value === null || value === "") return;

        if (option.type === "single-select" || option.type === "boolean") {
          where.push([field, "==", value]);
        } else if (option.type === "multi-select" && Array.isArray(value)) {
          value.forEach((v) => where.push([`${field}.${v}`, "==", true]));
        } else if (option.type === "number") {
          where.push([field, "==", parseInt(value, 10)]);
        } else if (option.type === "text") {
          where.push([field, "==", value]);
        }
      });
      return where;
    },
    runQuery() {
      this.pagination.currentPage = 1;
      this.sessionsByMatchId = {};
      this.fetchPastMatches();
    },
    prefetchMissingVenues(rows) {
      (rows || []).forEach((row) => {
        const venueId = row && row.ground;
        if (!venueId) return;
        const venue = this.$store.getters["venues/byId"](venueId);
        if (venue) return;
        if (this.requestedVenueIds[venueId]) return;
        this.$set(this.requestedVenueIds, venueId, true);
        this.$store.dispatch("venues/fetchById", venueId).catch(() => {});
      });
    },
    sessionsForMatch(row) {
      const matchId = this.matchDocId(row);
      if (!matchId) return [];
      return this.sessionsByMatchId[matchId] || [];
    },
    sessionSortMs(session) {
      const ts = session && (session.flow_started_at || session.session_started_at || session.activated_at || session.created_at);
      if (!ts) return 0;
      if (ts.toMillis) return ts.toMillis();
      if (ts.toDate) return ts.toDate().getTime();
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    },
    async loadSessionsForMatchIds(matchIds) {
      const ids = (matchIds || []).map((id) => String(id)).filter(Boolean);
      if (!ids.length) {
        this.sessionsByMatchId = {};
        this.sessionsLoadError = "";
        return;
      }
      this.sessionsLoading = true;
      this.sessionsLoadError = "";
      try {
        const db = Firebase.firestore();
        const map = {};
        const batchSize = 10;
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize);
          const snap = await db
            .collection("livestreams")
            .where("matchId", "in", batch)
            .get();
          snap.docs.forEach((doc) => {
            const data = { id: doc.id, ...doc.data() };
            const mid = data.matchId != null ? String(data.matchId) : "";
            if (!mid) return;
            if (!map[mid]) map[mid] = [];
            map[mid].push(data);
          });
        }
        Object.keys(map).forEach((matchId) => {
          map[matchId].sort((a, b) => this.sessionSortMs(b) - this.sessionSortMs(a));
        });
        this.sessionsByMatchId = map;
      } catch (e) {
        console.error("[StreamReporting] loadSessionsForMatchIds failed", e);
        this.sessionsLoadError = (e && e.message) || "Unable to load stream sessions";
        this.sessionsByMatchId = {};
      } finally {
        this.sessionsLoading = false;
      }
    },
    sessionTimestamp(ts) {
      if (!ts) return "—";
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/London",
      });
    },
    formatStreamDuration(ms) {
      if (!ms || ms <= 0) return "";
      const totalSec = Math.round(ms / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },
    sessionStatusLabel(session) {
      if (!session) return "Unknown";
      if (session.flow_lifecycle_status === "active" || session.session_status === "active") return "Live";
      return "Ended";
    },
    sessionStatusClass(session) {
      const live = session && (session.flow_lifecycle_status === "active" || session.session_status === "active");
      return live ? "badge-success" : "badge-secondary";
    },
    sessionInputLabel(session) {
      const snap = session && session.input_snapshot;
      if (snap && snap.name) return snap.name;
      if (session && session.inputId) {
        const input = this.$store.getters["inputs/byId"](session.inputId);
        if (input) return this.inputOptionLabel(input);
        return session.inputId;
      }
      return "—";
    },
    sessionTakersLabel(session) {
      const rows = (session && session.takers_snapshot) || [];
      if (!rows.length) return "—";
      return rows.map((t) => t.name || t.takerId).join(", ");
    },
    shutdownReasonLabel(reason) {
      const labels = {
        manual_stop: "Manual stop",
        activate_stream_deactivate: "Stream deactivated",
        scheduled_shutdown_match_end: "Scheduled shutdown",
        nightly_sweep_orphan: "Nightly sweep",
        match_page_flow_deactivate: "Match page stop",
        no_signal_timeout: "No signal timeout",
        source_disconnect_timeout: "Source disconnect timeout",
      };
      if (!reason) return "—";
      return labels[reason] || reason.replace(/_/g, " ");
    },
    matchTakerRows(row) {
      if (!row || !Array.isArray(row.livestreamTakerRoutes)) return [];
      return row.livestreamTakerRoutes.filter((r) => r && r.takerId && r.enabled !== false);
    },
    takerLabel(takerId) {
      const t = this.$store.getters["takers/byId"](takerId);
      return (t && (t.name || t.key)) || takerId;
    },
    inputOptionLabel(inp) {
      const type =
        inp.type === "mediaconnect"
          ? "MC"
          : inp.type === "medialive"
            ? "ML"
            : inp.type === "brightcove"
              ? "BC"
              : inp.type || "?";
      return `${inp.name || inp.id} [${type}]`;
    },
    inputNameForMatch(row) {
      if (!row || !row.livestreamInputId) return "";
      const input = this.$store.getters["inputs/byId"](row.livestreamInputId);
      if (!input) return row.livestreamInputId;
      return this.inputOptionLabel(input);
    },
  },
};
</script>

<style>
.el-table__expand-column {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.select-danger .el-select .el-tag {
  background: #f5365c;
}

.session-subtable {
  background: #f8f9fe;
}
</style>
